/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo, useState } from "react";
import { Resource } from "~/generated/graphql";
import { DEFAULT_PAGE_SIZE, LOAD_MORE_INCREMENT } from "~/constants/pagination";
import { useQueryState } from "~/hooks";
import { useGetAllGroupResources } from "../useGetAllGroupResources/useGetAllGroupResources";

type GroupInput = { id: string; name: string };

export function usePaginatedActionResources<R>(
  resourceGroups: GroupInput[],
  resources: R[],
  defaultVisibleCount: number = DEFAULT_PAGE_SIZE
) {
  const { resolvedGroups, loading } = useGetAllGroupResources(
    resourceGroups.map(({ id }) => id)
  );

  const { queryState, updateQueryState, removeQueryState } = useQueryState();
  const normalizedVisibleCount = Number(
    queryState.limit ?? defaultVisibleCount
  );
  const [visibleCount, setVisibleCount] = useState(
    isNaN(normalizedVisibleCount) ? defaultVisibleCount : normalizedVisibleCount
  );

  const groupResources = useMemo(
    () => resolvedGroups.flatMap((group) => group.resources),
    [resolvedGroups]
  );

  useEffect(() => {
    const nextVisibleCount = Number(queryState.limit ?? defaultVisibleCount);
    if (isNaN(nextVisibleCount)) {
      setVisibleCount(defaultVisibleCount);
      removeQueryState(["limit"], "replace");
    } else if (nextVisibleCount !== visibleCount) {
      setVisibleCount(nextVisibleCount);
    }
  }, [defaultVisibleCount, queryState.limit]);

  const resourceTotal = groupResources.length + resources.length;
  const hasPagination = resourceTotal > defaultVisibleCount;

  const { visibleResourceGroups, visibleResources } = useMemo(() => {
    let remaining = visibleCount;
    const nextVisibleResourceGroups: (GroupInput & {
      resources?: Resource[];
    })[] = [];

    for (const resourceGroup of resourceGroups) {
      if (remaining <= 0) {
        break;
      }
      const resolved = resolvedGroups.find(
        (group) => group.groupId === resourceGroup.id
      );
      const currentGroupResources = resolved?.resources ?? [];
      const visibleGroupResources = currentGroupResources.slice(0, remaining);
      if (visibleGroupResources.length > 0) {
        nextVisibleResourceGroups.push({
          ...resourceGroup,
          resources: visibleGroupResources,
        });
        remaining -= visibleGroupResources.length;
      }
    }

    return {
      visibleResourceGroups: nextVisibleResourceGroups,
      visibleResources: resources.slice(0, remaining),
    };
  }, [resourceGroups, resolvedGroups, resources, visibleCount]);

  const updateVisibleCount = (nextVisibleCount: number) => {
    setVisibleCount(nextVisibleCount);

    if (
      nextVisibleCount === defaultVisibleCount &&
      queryState.limit !== undefined
    ) {
      removeQueryState(["limit"], "replace");
      return;
    }

    updateQueryState({ limit: String(nextVisibleCount) }, "replace");
  };

  const loadMoreProps = {
    currentCount: Math.min(visibleCount, resourceTotal),
    total: resourceTotal,
    onLoadMore: () =>
      updateVisibleCount(
        Math.min(visibleCount + LOAD_MORE_INCREMENT, resourceTotal)
      ),
    onLoadLess:
      visibleCount > defaultVisibleCount
        ? () => updateVisibleCount(defaultVisibleCount)
        : undefined,
  };

  return {
    loading,
    hasPagination,
    groupResources,
    resourceTotal,
    visibleResourceGroups,
    visibleResources,
    loadMoreProps,
  };
}
